import { useEffect, useState } from 'react';

import {
  CircularProgress,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  type CurrencyCodeSelectOption,
  useGetCurrencyCodeOptionByIsoCode,
} from '@/api/typesense/currencyCodes';
import { Icon } from '@/components/ui/Icon';
import { InputFieldSkeletonLoader } from '@/components/ui/InputFieldSkeletonLoader';
import { uiIcons } from '@/constants/uiIcons';
import { isRequired } from '@/forms/schemas';
import { requiredNumber } from '@/forms/schemas/definitions';
import { withFieldGroup } from '@/forms/useAppForm';
import { useConvertCurrencies } from '@/hooks/useConvertCurrencies';
import { useFormatNumber } from '@/hooks/useFormatNumber';
import { useUserAccountHasFeature } from '@/hooks/useUserAccountHasFeature';
import { useUserAccountSettingValue } from '@/hooks/useUserAccountSettingValue';
import { ExpenseDetailsFormFields } from '@/pages/register/expenses/forms/ExpenseDetails';
import { ProjectInformationFormFields } from '@/pages/register/expenses/forms/ProjectInformation';
import { useExpenseFormBlurredValuesStore } from '@/pages/register/expenses/stores/expenseFormBlurredValues';
import { parseLocaleNumber } from '@/utils';

export type ProjectFinancialsFormFields = {
  projectCost: string;
  profitPercentage: string;
  billableAmount: string;
};

export const projectFinancialsDefaultValues: ProjectFinancialsFormFields = {
  projectCost: '',
  profitPercentage: '',
  billableAmount: '',
};

export const projectFinancialsSchema = z.object({
  projectCost: requiredNumber(),
  profitPercentage: requiredNumber(),
  billableAmount: requiredNumber(),
});

export type ProjectFinancialsOutput = z.infer<typeof projectFinancialsSchema>;

const ProjectCostHelperText = ({
  formattedExpenseAmountInTargetCurrency,
  formattedVatRatePercentage,
  formattedProjectCostAmount,
  expenseTargetCurrency,
  projectCurrency,
  isCurrencyConversionError,
}: {
  formattedExpenseAmountInTargetCurrency: string;
  formattedVatRatePercentage: string;
  formattedProjectCostAmount: string;
  expenseTargetCurrency: CurrencyCodeSelectOption;
  projectCurrency: CurrencyCodeSelectOption;
  isCurrencyConversionError: boolean;
}) => {
  const { t } = useTranslation('expenses');

  if (isCurrencyConversionError) {
    return (
      <Typography variant="label" color="error" role="alert">
        {t('projectFinancials.LoadCurrencyConversionErrors')}
      </Typography>
    );
  }

  if (
    !formattedExpenseAmountInTargetCurrency ||
    !formattedVatRatePercentage ||
    !formattedProjectCostAmount ||
    !expenseTargetCurrency ||
    !projectCurrency
  )
    return null;

  const helperTextString = `${formattedExpenseAmountInTargetCurrency} ${expenseTargetCurrency.isoCode} - ${formattedVatRatePercentage}% ${t('projectFinancials.VATRate')} = ${formattedProjectCostAmount} ${projectCurrency.isoCode}`;

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Icon
        name={uiIcons.status.info}
        size={14}
        color={scssVariables.ColorFog80}
      />
      <Typography variant="label" color="secondary" component="span">
        {helperTextString}
      </Typography>
    </Stack>
  );
};

interface ProjectFinancialsFormProps extends Record<string, unknown> {
  title: string;
  formLocked?: boolean;
  isParentLoading?: boolean;
  isEditPage?: boolean;
  expenseTargetCurrency: CurrencyCodeSelectOption;
  existingExpenseProjectCurrency?: CurrencyCodeSelectOption | null;
}

export const ProjectFinancialsForm = withFieldGroup({
  defaultValues: projectFinancialsDefaultValues,
  props: {
    // These default values are also for type-checking and are not used at runtime
    title: 'Title',
    formLocked: undefined,
    isParentLoading: undefined,
    isEditPage: false,
    expenseTargetCurrency: null, // the user's legal entity currency on new expense, the saved target currency of the existing expense on edit expense
    existingExpenseProjectCurrency: null,
  } as ProjectFinancialsFormProps,
  render: function Render({
    group,
    title,
    formLocked,
    isParentLoading,
    isEditPage,
    expenseTargetCurrency,
    existingExpenseProjectCurrency,
  }) {
    const { t } = useTranslation(['forms', 'expenses']);
    const { expenseDetailValues: blurredExpenseDetailValues } =
      useExpenseFormBlurredValuesStore();

    const {
      hasFeature: hasProfitOnExpenses,
      isLoading: isHasProfitOnExpensesLoading,
    } = useUserAccountHasFeature('profitOnExpenses');
    const {
      settingValue: _defaultProfitPercentage,
      isLoading: isProfitPercentageLoading,
    } = useUserAccountSettingValue('expenseProfitPercent');
    const { settingValue: profitRule, isLoading: isProfitRuleLoading } =
      useUserAccountSettingValue('expensesProfitRule');

    const defaultProfitPercentage =
      profitRule === 'Optional' ? 0 : _defaultProfitPercentage;

    const expenseFormIsUnchanged = useStore(
      group.form.store,
      (state) => state.isDefaultValue,
    );

    const expenseProjectIsUnchanged = useStore(
      group.form.store,
      (state) => state.fieldMeta['project_information.project']?.isDefaultValue,
    );

    const currentExpenseDetails = useStore(
      group.form.store,
      (state) =>
        (
          state.values as {
            expense_details: ExpenseDetailsFormFields;
          }
        )?.expense_details,
    );
    const currentProjectInformation = useStore(
      group.form.store,
      (state) =>
        (
          state.values as {
            project_information: ProjectInformationFormFields;
          }
        )?.project_information,
    );
    const isBillableSelected = currentExpenseDetails.billable;

    const { data: projectCurrency } = useGetCurrencyCodeOptionByIsoCode(
      currentProjectInformation.project?.currencyIsoCode,
    );

    /*
     * On edit expense, we use the saved project currency of the existing expense
     * as long as the project is unchanged (to also cover that the project's has change, but not the one of the expense). Otherwise, we need to use the new project's currency as on new expense
     *
     * On new expense, we use the project currency if a project is selected and it has a currency,
     * otherwise we fall back to the user's legal entity currency as target currency for conversion
     */
    const projectCostCurrency =
      isEditPage && expenseProjectIsUnchanged
        ? existingExpenseProjectCurrency
        : (projectCurrency ?? expenseTargetCurrency);

    const projectCostCurrencySameAsExpenseCurrency =
      projectCostCurrency &&
      currentExpenseDetails.expenseCurrency &&
      projectCostCurrency.isoCode ===
        currentExpenseDetails.expenseCurrency.isoCode;

    const displayProjectCostHelperText = !!(
      projectCostCurrency &&
      currentExpenseDetails.expenseCurrency &&
      blurredExpenseDetailValues.amountIncludingVat &&
      !projectCostCurrencySameAsExpenseCurrency
    );

    // use onBlur values from store for correct update behavior
    const amountIncludingVatNumber = parseLocaleNumber(
      blurredExpenseDetailValues.amountIncludingVat,
    );
    const vatAmountNumber = parseLocaleNumber(
      blurredExpenseDetailValues.vatAmount,
    );

    /*
     * When using currency conversion, we need to calculate the VAT factor from the expense currency amounts and
     * deduct that from the manually modifiable target currency converted amount
     */
    const expenseVatFactor = vatAmountNumber / amountIncludingVatNumber || 0;
    const expenseVatPercentage =
      (vatAmountNumber / (amountIncludingVatNumber - vatAmountNumber)) * 100 ||
      0;

    const decimalFormattedExpenseVatPercentage = useFormatNumber(
      expenseVatPercentage,
      {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      },
    );

    const targetCurrencyConvertedAmount = parseLocaleNumber(
      blurredExpenseDetailValues.targetCurrencyConvertedAmount,
    );

    // target currency is the user's legal entity currency NOT the project currency (but they can be equal)
    const projectCostInTargetCurrency =
      targetCurrencyConvertedAmount -
      targetCurrencyConvertedAmount * expenseVatFactor;

    const {
      amount: projectCostInProjectCurrencyBasedOnTargetCurrencyConversion,
      isLoading: isLoadingProjectCostInProjectCurrency,
      isError: isErrorProjectCostInProjectCurrency,
    } = useConvertCurrencies(
      projectCostInTargetCurrency,
      expenseTargetCurrency,
      'project',
      currentExpenseDetails.date,
      projectCostCurrency,
    );

    /*
     * When the project cost currency is the same as the expense currency, we follow the existing business logic to just use
     * the actual entered amount minus VAT as project cost, without respecting the possible modified exchange rate or any differing
     * currency conversion through the CurrencyMatrix feature.
     *
     * This is because the invoiced customers to only pay the original expense amount (plus potential profit), even when the user internally
     * has different costs due to differing target currency (legal entity's currency) conversions.
     *
     * Due to React Rule-of-Hooks, we still do the calculations for target currency conversions and just disregard them here.
     */
    const projectCostInProjectCurrency =
      projectCostCurrencySameAsExpenseCurrency
        ? amountIncludingVatNumber - vatAmountNumber
        : projectCostInProjectCurrencyBasedOnTargetCurrencyConversion;

    const decimalFormattedProjectCost = useFormatNumber(
      projectCostInProjectCurrency,
    );

    const decimalFormattedZero = useFormatNumber(0);

    const decimalFormattedDefaultProfitPercentage = useFormatNumber(
      defaultProfitPercentage || 0,
    );

    const profitPercentage = useStore(
      group.store,
      (state) => state.values.profitPercentage,
    );
    const decimalFormattedProfitPercentage = useFormatNumber(
      parseLocaleNumber(profitPercentage),
    );

    const billableAmount = useStore(
      group.store,
      (state) => state.values.billableAmount,
    );
    const decimalFormattedBillableAmount = useFormatNumber(
      parseLocaleNumber(billableAmount),
    );

    const settingsLoading =
      isProfitPercentageLoading ||
      isHasProfitOnExpensesLoading ||
      isProfitRuleLoading;

    const [
      billableAmountCalculatedFromLinkedFields,
      setBillableAmountCalculatedFromLinkedFields,
    ] = useState<number | null>(null);

    const decimalFormattedBillableAmountCalculatedFromLinkedFields =
      useFormatNumber(billableAmountCalculatedFromLinkedFields || 0);

    const [
      profitPercentageCalculatedFromLinkedFields,
      setProfitPercentageCalculatedFromLinkedFields,
    ] = useState<number | null>(null);

    const decimalFormattedProfitPercentageCalculatedFromLinkedFields =
      useFormatNumber(profitPercentageCalculatedFromLinkedFields || 0);

    useEffect(() => {
      if (
        billableAmountCalculatedFromLinkedFields !== null &&
        !isNaN(billableAmountCalculatedFromLinkedFields)
      ) {
        if (!(isEditPage && expenseFormIsUnchanged)) {
          group.setFieldValue(
            'billableAmount',
            decimalFormattedBillableAmountCalculatedFromLinkedFields,
          );
        }
      }
    }, [
      billableAmountCalculatedFromLinkedFields,
      decimalFormattedBillableAmountCalculatedFromLinkedFields,
    ]);

    useEffect(() => {
      if (
        profitPercentageCalculatedFromLinkedFields !== null &&
        !isNaN(profitPercentageCalculatedFromLinkedFields)
      ) {
        if (!(isEditPage && expenseFormIsUnchanged)) {
          group.setFieldValue(
            'profitPercentage',
            decimalFormattedProfitPercentageCalculatedFromLinkedFields,
          );
        }
      }
    }, [
      profitPercentageCalculatedFromLinkedFields,
      decimalFormattedProfitPercentageCalculatedFromLinkedFields,
    ]);

    const autoCalculateFieldsOnProjectCostChange = (
      costValue: number | null,
      profitPercentageToUse: number | null = null,
    ) => {
      if (
        !settingsLoading &&
        costValue !== null &&
        hasProfitOnExpenses &&
        projectCostInProjectCurrency !== null &&
        !isNaN(projectCostInProjectCurrency)
      ) {
        if (
          defaultProfitPercentage !== undefined &&
          defaultProfitPercentage !== null
        ) {
          let profitPercentageForCalculation =
            profitPercentageToUse ?? parseLocaleNumber(profitPercentage);

          if (
            isNaN(profitPercentageForCalculation) &&
            !isNaN(defaultProfitPercentage)
          ) {
            group.setFieldValue(
              'profitPercentage',
              isBillableSelected
                ? decimalFormattedDefaultProfitPercentage
                : decimalFormattedZero,
            );
            profitPercentageForCalculation = defaultProfitPercentage;
          }

          if (!isNaN(profitPercentageForCalculation) && isBillableSelected) {
            setBillableAmountCalculatedFromLinkedFields(
              costValue * (1 + profitPercentageForCalculation / 100),
            );
          } else {
            group.setFieldValue('billableAmount', decimalFormattedZero);
          }
        }
      }
    };

    useEffect(() => {
      if (!(isEditPage && expenseFormIsUnchanged)) {
        if (
          !isLoadingProjectCostInProjectCurrency &&
          projectCostInProjectCurrency !== null &&
          !isNaN(projectCostInProjectCurrency)
        ) {
          group.setFieldValue('projectCost', decimalFormattedProjectCost);
        }

        if (
          isErrorProjectCostInProjectCurrency &&
          !isLoadingProjectCostInProjectCurrency
        ) {
          // reset UI when currency conversion fails
          group.resetField('projectCost');
          group.resetField('profitPercentage');
          group.resetField('billableAmount');
          setProfitPercentageCalculatedFromLinkedFields(null);
          setBillableAmountCalculatedFromLinkedFields(null);
        }
      }
    }, [
      decimalFormattedProjectCost,
      projectCostInProjectCurrency,
      isLoadingProjectCostInProjectCurrency,
      isErrorProjectCostInProjectCurrency,
    ]);

    useEffect(() => {
      if (!(isEditPage && expenseFormIsUnchanged)) {
        autoCalculateFieldsOnProjectCostChange(projectCostInProjectCurrency);
      }
    }, [
      projectCostInProjectCurrency,
      settingsLoading,
      defaultProfitPercentage,
      profitRule,
      hasProfitOnExpenses,
    ]);

    const updateBillableAmount = () => {
      if (!projectCostInProjectCurrency) return;

      const newBillableAmount =
        projectCostInProjectCurrency *
        (1 + parseLocaleNumber(profitPercentage) / 100);

      setBillableAmountCalculatedFromLinkedFields(newBillableAmount);
    };

    const updateProfitPercentage = () => {
      if (!projectCostInProjectCurrency) return;

      const newProfitPercentage =
        ((parseLocaleNumber(billableAmount) - projectCostInProjectCurrency) /
          projectCostInProjectCurrency) *
        100;

      setProfitPercentageCalculatedFromLinkedFields(newProfitPercentage);
    };

    // field states
    const fieldsDisabled = formLocked || isParentLoading;
    const profitPercentageFieldDisabled =
      fieldsDisabled || profitRule === 'Fixed' || !isBillableSelected;
    const billableAmountFieldDisabled =
      fieldsDisabled || profitRule === 'Fixed' || !isBillableSelected;

    const [previousProfitPercentage, setPreviousProfitPercentage] =
      useState<string>('');
    const [initialLoadingDone, setInitialLoadingDone] =
      useState<boolean>(false);

    useEffect(() => {
      /*
       * Default form value of billable is null, so that we can distinct between billable false on edit expense and data not loaded yet
       * On edit page we do not want to run the logic before initial load is done to not override values coming from the expense being edited
       * Loading and propagation of saved isBillable value is done when this useEffect ran once (old: null, new: true/false from existing expense to edit)
       *
       * On new expense, we know that any effect to isBillable is from user input (through checkbox or picking a project/ subcontract), so we do not
       * need to block the first run
       */
      if (isEditPage && isBillableSelected === null) return;
      if (isEditPage && !initialLoadingDone) {
        setInitialLoadingDone(true);
        return;
      }
      // only run when project cost was already set
      if (
        projectCostInProjectCurrency &&
        !isNaN(projectCostInProjectCurrency)
      ) {
        if (!isBillableSelected) {
          // Resetting fields when billable gets unselected after an expense amount was entered
          // store previous values to restore if billable is re-selected

          setPreviousProfitPercentage(
            group.getFieldValue('profitPercentage') ?? '',
          );

          // for reactivity reasons we need to set the fields directly and the auto-calculated values to 0 to make sure they get updated and displayed correctly when (re-)selecting billable
          group.setFieldValue('profitPercentage', decimalFormattedZero);
          setProfitPercentageCalculatedFromLinkedFields(0);
          group.setFieldValue('billableAmount', decimalFormattedZero);
          setBillableAmountCalculatedFromLinkedFields(0);
        } else {
          // when billable is selected, recalculate based on project cost and last set profit percentage
          // when no previous values are stored, use default profit percentage setting (as billable was never toggled yet)
          const profitPercentageValue =
            previousProfitPercentage !== ''
              ? previousProfitPercentage
              : decimalFormattedDefaultProfitPercentage;
          group.setFieldValue('profitPercentage', profitPercentageValue);
          // we cannot use the previous billable amount here as it might not match the current project cost or expense currency anymore, so we calculate it based on profit percentage
          autoCalculateFieldsOnProjectCostChange(
            projectCostInProjectCurrency,
            Number(profitPercentageValue),
          );
        }
      }
    }, [isBillableSelected]);

    return (
      <group.FormFieldset legend={title} filled>
        <group.FormGrid>
          {isParentLoading ? (
            [1, 2, 3].map((index) => <InputFieldSkeletonLoader key={index} />)
          ) : (
            <>
              <group.AppField name="projectCost">
                {(Field) => (
                  <Field.TextInputField
                    required={isRequired(
                      projectFinancialsSchema.shape.projectCost,
                    )}
                    label={t('labels.ProjectCost')}
                    type="number"
                    disabled={fieldsDisabled}
                    readOnly
                    endAdornment={
                      <InputAdornment position="end">
                        <Stack direction="row" alignItems="center" gap={1}>
                          {isLoadingProjectCostInProjectCurrency && (
                            <CircularProgress
                              size={18}
                              color="inherit"
                              thickness={4}
                            />
                          )}
                          <Typography color="textSecondary">
                            {projectCostCurrency?.isoCode}
                          </Typography>
                        </Stack>
                      </InputAdornment>
                    }
                    helperText={
                      displayProjectCostHelperText && (
                        <ProjectCostHelperText
                          formattedExpenseAmountInTargetCurrency={
                            blurredExpenseDetailValues.targetCurrencyConvertedAmount ??
                            ''
                          }
                          formattedVatRatePercentage={
                            decimalFormattedExpenseVatPercentage
                          }
                          formattedProjectCostAmount={
                            decimalFormattedProjectCost
                          }
                          expenseTargetCurrency={expenseTargetCurrency}
                          projectCurrency={projectCostCurrency}
                          isCurrencyConversionError={
                            isErrorProjectCostInProjectCurrency ?? false
                          }
                        />
                      )
                    }
                  />
                )}
              </group.AppField>
              {hasProfitOnExpenses && (
                <>
                  <group.AppField
                    name="profitPercentage"
                    listeners={{
                      onBlur: () => {
                        if (
                          group.getFieldMeta('profitPercentage')?.isValid &&
                          group.getFieldMeta('profitPercentage')?.isDirty
                        ) {
                          group.setFieldValue(
                            'profitPercentage',
                            decimalFormattedProfitPercentage,
                          );
                          // recalculate billable amount based on new profit percentage
                          updateBillableAmount();
                        }
                      },
                    }}
                  >
                    {(Field) => (
                      <Field.TextInputField
                        required={isRequired(
                          projectFinancialsSchema.shape.profitPercentage,
                        )}
                        label={t('labels.ProfitPercentage')}
                        type="number"
                        disabled={profitPercentageFieldDisabled}
                        readOnly={profitRule === 'Fixed'}
                        endAdornment={
                          <InputAdornment position="end">%</InputAdornment>
                        }
                      />
                    )}
                  </group.AppField>
                  <group.AppField
                    name="billableAmount"
                    listeners={{
                      onBlur: () => {
                        if (
                          group.getFieldMeta('billableAmount')?.isValid &&
                          group.getFieldMeta('billableAmount')?.isDirty
                        ) {
                          group.setFieldValue(
                            'billableAmount',
                            decimalFormattedBillableAmount,
                          );
                          // recalculate profit percentage based on new billable amount
                          updateProfitPercentage();
                        }
                      },
                    }}
                  >
                    {(Field) => (
                      <Field.TextInputField
                        required={isRequired(
                          projectFinancialsSchema.shape.billableAmount,
                        )}
                        label={t('labels.BillableAmount')}
                        type="number"
                        disabled={billableAmountFieldDisabled}
                        readOnly={profitRule === 'Fixed'}
                        endAdornment={
                          <InputAdornment position="end">
                            <Stack direction="row" alignItems="center" gap={1}>
                              {isLoadingProjectCostInProjectCurrency && (
                                <CircularProgress
                                  size={18}
                                  color="inherit"
                                  thickness={4}
                                />
                              )}
                              <Typography color="textSecondary">
                                {projectCostCurrency?.isoCode}
                              </Typography>
                            </Stack>
                          </InputAdornment>
                        }
                      />
                    )}
                  </group.AppField>
                </>
              )}
            </>
          )}
        </group.FormGrid>
      </group.FormFieldset>
    );
  },
});
